import { FileText, RefreshCw, Rss, Send, Upload } from "lucide-react-native";
import { FC, useCallback, useMemo, useState } from "react";
import { ScrollView } from "react-native";

import { Summary } from "@/components/questions/results";
import {
  Box,
  Button,
  ButtonIcon,
  ButtonText,
  HStack,
  Pressable,
  Spinner,
  Text,
  VStack,
} from "@/components/ui";

import { formatTime } from "./colors";
import { Segmented } from "./Controls";
import DistrictList from "./DistrictList";
import FieldWaveSheet from "./FieldWaveSheet";
import { STATES } from "./geo";
import MethodSheet from "./MethodSheet";
import { nationalEnvironment, nationalGeneric, turnoutEdge } from "./model";
import NationalSummary from "./NationalSummary";
import Overview from "./Overview";
import PublishSheet from "./PublishSheet";
import RaceList from "./RaceList";
import RacePanel from "./RacePanel";
import { RACES } from "./races";
import RaceSummary from "./RaceSummary";
import ResultsOverview from "./ResultsOverview";
import SourcesSheet from "./SourcesSheet";
import { raceFigures, raceKey } from "./summary";
import TrackerMap from "./TrackerMap";
import type {
  Layer,
  ModelSettings,
  Office,
  PublishedSummary,
  SnapshotSummariesByWeight,
  StateSample,
  TrackerVariant,
} from "./types";
import useCounty2024 from "./useCounty2024";
import type { ExternalSources } from "./useExternalSources";
import type { MidtermsData } from "./useMidtermsData";
import {
  buildDistrictRows,
  buildRows,
  districtPaints,
  GOVERNOR_HOLDOVERS,
  governorCounts,
  houseProjection,
  nationalPaints,
  OFFICE_LABEL,
  SENATE_HOLDOVERS,
  senateProjection,
  tallyDistricts,
  tallyRaces,
} from "./view";

/* ── Types ─────────────────────────────────────── */

/** Where the tracker's data comes from, and what the viewer can do. */
type TrackerMode =
  | {
      /** Published forecasts and live results to compare Foresight with. */
      comparison: ExternalSources;
      /** The tracker studies' latest runs, loaded live. */
      data: MidtermsData;
      variant: "admin";
    }
  | {
      publishedAt: string;
      samples: Record<string, StateSample>;
      summaries: SnapshotSummariesByWeight;
      variant: "client";
    };

interface MidtermsTrackerProps {
  mode: TrackerMode;
  onSettingsChange: (settings: ModelSettings) => void;
  settings: ModelSettings;
}

type SheetId = "method" | "publish" | "sources" | "wave";

/* ── Constants ─────────────────────────────────── */

const DISCLAIMERS: Record<TrackerVariant, string> = {
  admin:
    "Internal tool, visible to Foresight admins only. Reads come from Foresight's synthetic respondents, not a poll of real voters, calibrated to certified 2024 results. Treat them as an experimental signal next to published forecasts.",
  client:
    "Reads come from Foresight's synthetic respondents, not a poll of real voters, calibrated to certified 2024 results.",
};

const OFFICES: { label: string; value: Office }[] = [
  { label: "Senate", value: "senate" },
  { label: "Governor", value: "governor" },
  { label: "House", value: "house" },
];

/* ── Subcomponents ─────────────────────────────── */

/** Key insights read from the published snapshot; nothing when missing. */
const PublishedInsights: FC<{ summary: PublishedSummary | undefined }> = ({
  summary,
}) =>
  summary && summary.summary !== null ? (
    <Summary summary={{ ...summary, visualization: null }} />
  ) : null;

/* ── Component ─────────────────────────────────── */

/** The tracker page body, shared by the admin and client views. */
const MidtermsTracker: FC<MidtermsTrackerProps> = ({
  mode,
  onSettingsChange,
  settings,
}) => {
  const { county2024, error: countyError } = useCounty2024();

  const [office, setOffice] = useState<Office>("senate");
  const [selected, setSelected] = useState<string | null>(null);
  const [sheet, setSheet] = useState<SheetId | null>(null);

  const admin = mode.variant === "admin" ? mode : null;
  const samples = mode.variant === "admin" ? mode.data.samples : mode.samples;
  const published =
    mode.variant === "client"
      ? mode.summaries[String(settings.ownWeight)]
      : undefined;
  const activeSource = admin?.comparison.activeSource ?? null;
  const activeLayer: Layer = admin?.comparison.activeLayer ?? "foresight";
  const sourceLabel = activeSource?.label ?? null;
  const results = activeSource?.results === true;

  const edge = useMemo(() => turnoutEdge(samples), [samples]);
  const environment = useMemo(
    () => nationalEnvironment(samples, { county2024, edge, settings }),
    [county2024, edge, samples, settings]
  );
  const rows = useMemo(
    () =>
      buildRows({
        county2024,
        edge,
        environment,
        external: activeSource,
        layer: activeLayer,
        office,
        races: RACES,
        samples,
        settings,
      }),
    [
      activeLayer,
      activeSource,
      county2024,
      edge,
      environment,
      office,
      samples,
      settings,
    ]
  );
  const paints = useMemo(
    () => nationalPaints(rows, activeLayer, office, sourceLabel, results),
    [activeLayer, office, results, rows, sourceLabel]
  );
  const senate = useMemo(
    () =>
      office === "senate"
        ? senateProjection(rows, activeLayer, settings)
        : null,
    [activeLayer, office, rows, settings]
  );
  const governors = useMemo(
    () => (office === "governor" ? governorCounts(rows) : null),
    [office, rows]
  );
  const foresightGeneric = useMemo(
    () =>
      office === "house"
        ? nationalGeneric(
            rows.flatMap((r) =>
              r.estimate ? [{ estimate: r.estimate, state: r.state }] : []
            ),
            STATES.filter((s) => s.abbr !== "DC")
          )
        : null,
    [office, rows]
  );
  const externalGeneric =
    activeSource?.rows.find((r) => r.office === "house" && r.state === "US")
      ?.margin ?? null;
  const districtRows = useMemo(
    () =>
      office === "house"
        ? buildDistrictRows({
            county2024,
            edge,
            external: activeSource,
            layer: activeLayer,
            samples,
            settings,
          })
        : [],
    [activeLayer, activeSource, county2024, edge, office, samples, settings]
  );
  const house = useMemo(
    () => (office === "house" ? houseProjection(districtRows) : null),
    [districtRows, office]
  );
  const districtMapPaints = useMemo(
    () => districtPaints(districtRows, activeLayer, sourceLabel, results),
    [activeLayer, districtRows, results, sourceLabel]
  );
  const tally = useMemo(
    () =>
      !results
        ? null
        : office === "house"
          ? tallyDistricts(districtRows)
          : tallyRaces(
              rows,
              office === "senate" ? SENATE_HOLDOVERS : GOVERNOR_HOLDOVERS
            ),
    [districtRows, office, results, rows]
  );
  const houseFromNational =
    activeLayer === "external"
      ? 0
      : districtRows.filter((r) => r.projection?.basis === "national").length;

  const selectedRow = selected
    ? (rows.find((r) => r.state.abbr === selected) ?? null)
    : null;
  const stateDistricts = useMemo(
    () =>
      selectedRow
        ? districtRows.filter(
            (r) => r.district.state === selectedRow.state.abbr
          )
        : [],
    [districtRows, selectedRow]
  );
  // The run behind the open state's survey: its race summary is written on
  // demand from it (admin view).
  const run =
    admin && selectedRow?.sample
      ? admin.data.samples[selectedRow.state.abbr]
      : null;
  const figures = useMemo(
    () =>
      run && selectedRow
        ? raceFigures({
            county2024,
            edge,
            environment,
            office,
            row: selectedRow,
            settings,
          })
        : null,
    [county2024, edge, environment, office, run, selectedRow, settings]
  );
  const questionId = run?.questionIds[office];

  const surveyed = rows.filter((r) => r.estimate).length;
  const latest = Object.values(samples)
    .map((s) => s.completedAt)
    .sort()
    .pop();
  const stamp =
    mode.variant === "client"
      ? ` · published ${formatTime(mode.publishedAt)}`
      : `${latest ? ` · latest run ${formatTime(latest)}` : ""}${results && activeSource ? ` · count as of ${formatTime(activeSource.importedAt)}` : ""}`;
  const loading = admin?.data.loading ?? false;
  const progress = admin?.data.progress ?? null;
  const showIndependent = rows.some((r) => r.leftParty === "I");
  // Seat summaries read Foresight under the Gap layer.
  const layerLabel =
    activeLayer === "external"
      ? (sourceLabel ?? "External")
      : activeLayer === "ensemble"
        ? "Ensemble"
        : "Foresight";

  const selectByFips = (fips: string) => {
    const state = STATES.find((s) => s.fips === fips);
    if (state && rows.some((r) => r.state.abbr === state.abbr)) {
      setSelected(state.abbr);
    }
  };

  const closeSheet = useCallback(() => setSheet(null), []);

  return (
    <VStack className="flex-1 min-h-0 gap-5">
      {/* Controls */}
      <HStack className="flex-col xl:flex-row xl:items-center gap-3">
        <HStack className="flex-wrap items-center gap-3 flex-1">
          <Segmented
            onChange={(next) => {
              setOffice(next);
              setSelected(null);
            }}
            options={OFFICES}
            value={office}
          />
          {admin && (
            <>
              <Box className="hidden lg:flex w-px h-6 bg-outline-200" />
              <Segmented
                onChange={admin.comparison.setLayer}
                options={admin.comparison.layerOptions}
                value={activeLayer}
              />
            </>
          )}
        </HStack>
        <HStack className="flex-wrap items-center gap-2">
          {admin && (
            <Button
              onPress={() => setSheet("sources")}
              size="sm"
              variant="outline"
            >
              <ButtonIcon as={Rss} />
              <ButtonText>Sources</ButtonText>
            </Button>
          )}
          <Button
            onPress={() => setSheet("method")}
            size="sm"
            variant="outline"
          >
            <ButtonIcon as={FileText} />
            <ButtonText>Method</ButtonText>
          </Button>
          {admin && (
            <>
              <Button
                isDisabled={loading}
                onPress={admin.data.refresh}
                size="sm"
                variant="outline"
              >
                <ButtonIcon as={RefreshCw} />
                <ButtonText>Refresh</ButtonText>
              </Button>
              <Button
                className="rounded-xl"
                onPress={() => setSheet("wave")}
                size="sm"
              >
                <ButtonIcon as={Send} />
                <ButtonText>Field wave</ButtonText>
              </Button>
              <Button
                className="rounded-xl"
                onPress={() => setSheet("publish")}
                size="sm"
              >
                <ButtonIcon as={Upload} />
                <ButtonText>Publish</ButtonText>
              </Button>
            </>
          )}
        </HStack>
      </HStack>

      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        <VStack className="gap-5 pb-8">
          {/* Status */}
          <HStack className="items-center gap-2 flex-wrap">
            {loading && (
              <Spinner color="rgb(var(--color-primary-500))" size="small" />
            )}
            <Text className="text-xs text-typography-500">
              {loading
                ? progress
                  ? `Loading surveys ${progress.done}/${progress.total}…`
                  : "Looking for tracker studies…"
                : `${surveyed}/${rows.length} ${office === "house" ? "states" : `${OFFICE_LABEL[office]} races`} surveyed${stamp}`}
            </Text>
            {admin?.data.error && (
              <Pressable onPress={admin.data.refresh}>
                <Text className="text-xs text-error-600">
                  {admin.data.error} · retry
                </Text>
              </Pressable>
            )}
            {countyError && (
              <Text className="text-xs text-typography-400">
                2024 county results unavailable ({countyError}); state-level
                calibration only.
              </Text>
            )}
            {admin?.comparison.resultsError && (
              <Text className="text-xs text-typography-400">
                Live results unavailable ({admin.comparison.resultsError});
                retrying every minute.
              </Text>
            )}
          </HStack>

          {tally ? (
            <ResultsOverview
              generic={
                office === "house"
                  ? {
                      count: externalGeneric,
                      foresight: foresightGeneric?.margin ?? null,
                    }
                  : null
              }
              office={office}
              tally={tally}
            />
          ) : (
            <Overview
              externalGeneric={externalGeneric}
              foresightGeneric={foresightGeneric}
              governors={governors}
              house={house}
              houseFromNational={houseFromNational}
              layerLabel={layerLabel}
              office={office}
              senate={senate}
              sourceLabel={sourceLabel}
              topline={activeSource?.topline ?? null}
              variant={mode.variant}
            />
          )}

          {/* Key insights: the selected race's, else the nation's */}
          {selectedRow ? (
            admin ? (
              figures && run && questionId ? (
                <RaceSummary
                  figures={figures}
                  key={`${run.runId}-${office}`}
                  questionId={questionId}
                  runId={run.runId}
                  studyId={run.studyId}
                />
              ) : null
            ) : (
              <PublishedInsights
                summary={published?.races[raceKey(office, selectedRow)]}
              />
            )
          ) : admin ? (
            <NationalSummary
              county2024={county2024}
              edge={edge}
              environment={environment}
              office={office}
              paused={loading || (!county2024 && !countyError)}
              samples={samples}
              settings={settings}
            />
          ) : (
            <PublishedInsights summary={published?.national[office]} />
          )}

          <HStack className="flex-col xl:flex-row gap-5 items-start">
            <TrackerMap
              county2024={county2024}
              districtPaints={districtMapPaints}
              edge={edge}
              environment={environment}
              layer={activeLayer}
              office={office}
              onBack={() => setSelected(null)}
              onSelect={selectByFips}
              paints={paints}
              results={results}
              row={selectedRow}
              settings={settings}
              showIndependent={showIndependent}
              sourceLabel={sourceLabel}
            />

            <VStack className="w-full xl:w-[460px] xl:shrink-0 gap-5">
              {selectedRow ? (
                <RacePanel
                  county2024={county2024}
                  edge={edge}
                  environment={environment}
                  office={office}
                  onClose={() => setSelected(null)}
                  results={results && office !== "house"}
                  row={selectedRow}
                  settings={settings}
                  // The House count is read by district, in the list below.
                  sourceLabel={
                    results && office === "house" ? null : sourceLabel
                  }
                  studyId={run?.studyId ?? null}
                  variant={mode.variant}
                />
              ) : (
                <>
                  {office === "house" && (
                    <DistrictList
                      onSelect={setSelected}
                      results={results}
                      rows={districtRows}
                      sourceLabel={sourceLabel}
                      title={`House seats, ${results ? "misses" : "closest"} first`}
                    />
                  )}
                  {!(results && office === "house") && (
                    <RaceList
                      office={office}
                      onSelect={setSelected}
                      results={results}
                      rows={rows}
                      sourceLabel={sourceLabel}
                    />
                  )}
                </>
              )}
              {selectedRow && office === "house" && (
                <DistrictList
                  onSelect={null}
                  results={results}
                  rows={stateDistricts}
                  sourceLabel={sourceLabel}
                  title={`${selectedRow.state.name} districts`}
                />
              )}
            </VStack>
          </HStack>

          <Text className="text-xs text-typography-400 text-center">
            {DISCLAIMERS[mode.variant]}
          </Text>
        </VStack>
      </ScrollView>

      <MethodSheet
        edge={edge}
        onChange={onSettingsChange}
        onClose={closeSheet}
        open={sheet === "method"}
        races={RACES}
        settings={settings}
        state={selected}
        variant={mode.variant}
      />
      {admin && (
        <>
          <SourcesSheet
            activeId={admin.comparison.activeSourceId}
            defaultOffice={office}
            onActivate={admin.comparison.activateSource}
            onClose={closeSheet}
            onDelete={admin.comparison.deleteSource}
            onSave={admin.comparison.saveSource}
            open={sheet === "sources"}
            races={RACES}
            sources={admin.comparison.allSources}
          />
          <FieldWaveSheet
            listed={admin.data.listed}
            onClose={closeSheet}
            onLaunched={admin.data.refresh}
            open={sheet === "wave"}
            races={RACES}
            studies={admin.data.studies}
          />
          <PublishSheet
            county2024={county2024}
            countyError={countyError}
            edge={edge}
            loading={loading}
            onClose={closeSheet}
            open={sheet === "publish"}
            samples={admin.data.samples}
          />
        </>
      )}
    </VStack>
  );
};

export default MidtermsTracker;
